/**
 * CellCelebration — one-shot micro-animation that fires when a score is
 * added to a cell.
 *
 *   1) The cell briefly pulses with the company's primary color (~600ms).
 *   2) A small "+$amount" floater rises 28px above the cell center and
 *      fades over ~900ms.
 *
 * Mount with a unique `triggerKey` to fire the animation. The component
 * auto-unmounts itself after the animation completes — the parent just
 * needs to bump `triggerKey` to fire again.
 *
 * Honors `prefers-reduced-motion: reduce` — when set, both animations
 * are skipped and the component renders nothing.
 */
import React, { useEffect, useState } from "react";
import SoftBox from "../SoftBox";
import { UseCompanyColors } from "../../hooks/company";

const FONT = `"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif`;

// Convert a hex like "#2563EB" to "rgba(37,99,235,a)". Defensive — falls
// back to a sensible blue so the component never crashes the cell.
const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
    return `rgba(37,99,235,${alpha})`;
  }
  let h = hex.slice(1);
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("");
  }
  if (h.length !== 6) return `rgba(37,99,235,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const prefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * @param {string|number} triggerKey — bump this to fire a new celebration.
 *   The same key won't re-fire (idempotent).
 * @param {string} amount — optional pre-formatted display value
 *   (e.g. "+$1,200", "+5"). If absent, only the pulse plays.
 */
export default function CellCelebration({ triggerKey, amount }) {
  const { primaryColor } = UseCompanyColors() || {};
  const primary = primaryColor || "#2563EB";

  const [activeKey, setActiveKey] = useState(null);

  // Fire when the trigger key changes.
  useEffect(() => {
    if (triggerKey == null) return undefined;
    if (prefersReducedMotion()) return undefined;
    setActiveKey(triggerKey);
    const t = window.setTimeout(() => setActiveKey(null), 1000);
    return () => window.clearTimeout(t);
  }, [triggerKey]);

  if (activeKey == null) return null;

  return (
    <>
      {/* Cell pulse — sits inside the cell as a 1-shot overlay. */}
      <SoftBox
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          background: hexToRgba(primary, 0.18),
          animation: "scorecellPulse 600ms ease-out forwards",
          zIndex: 1,
        }}
      />

      {/* Floater — rises and fades above the cell center. */}
      {amount && (
        <SoftBox
          aria-hidden
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: "14px",
            color: primary,
            whiteSpace: "nowrap",
            animation: "scorecellFloater 900ms ease-out forwards",
            zIndex: 2,
          }}
        >
          {amount}
        </SoftBox>
      )}

      {/* Keyframes — inlined so the component is fully self-contained. */}
      <style>{`
        @keyframes scorecellPulse {
          0%   { opacity: 0; }
          25%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes scorecellFloater {
          0%   { transform: translate(-50%, -50%); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(-50%, calc(-50% - 28px)); opacity: 0; }
        }
      `}</style>
    </>
  );
}
